import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Divider } from '@mui/material';

import useFormik from 'hooks/useFormik';
import {
  dotMedicalCardDefault,
  dotMedicalCardDefaultPolygon,
  dotMedicalCardDefaultVerification,
} from 'common/document/ui/driver-document';
import { IDocumentDetailField } from 'common/document/interfaces/document-detail-field';
import { IDocument } from 'common/document/interfaces/document';
import { IRejectReasonDocument } from 'common/request/interfaces/reject-reson-document';
import { LogEvents } from 'components/Logs/events';
import {
  clientDateToServerDate,
  clientDateToServerDateString,
} from 'functions/date/clientDateToServerDate';
import { buildFieldVerified } from '../Utils/field-verified';
import { buildFieldPolygon } from '../Utils/field-polygon';
import { buildVerificationResult } from '../Utils/verification-result';
import { DocumentImageViewer } from '../DocumentImageViewer';
import { DocumentReviewHeader } from '../DocumentViewHeader';
import { DOTMedicalCardForm } from '../DOTMedicalCardFrom';
import { validationSchema } from './validationSchema';
import {
  DocumentStatusEnum,
  readOnlyViewableStatuses,
} from 'common/document/enums/document-status.enum';
import { DocumentFiles } from 'common/document/interfaces/document-files';
import { DocumentDetailFieldStatusEnum } from 'common/document/enums/document-detail-field-status.enum';
import { originalSuffix } from 'common/document/constants/original-suffix';
import { DocumentImageCropper } from '../DocumentImageCropper';
import { IUpdateDocumentImage } from '../DocumentReviewContainer';
import { FileDTO } from 'store/candidate/interfaces';

import styles from './styles.module.scss';
import { OriginalFileParams } from '../DocumentReviewContainer/types';
import { defaultOriginalFileParams } from '../DocumentReviewContainer/constants';
import { AnimatedLoaderModal } from '../../../../components/Loaders/AnimatedLoader';
import { canApproveDotMedical } from './Utils/can-approve-dot-medical';
import { buildVerificationStatus } from '../Utils/verification-status';
import { AlertColor } from '@mui/material/Alert/Alert';
import { ReactComponent as DocumentHiddenIcon } from '../../../../assets/images/document-hidden.svg';
import { useManager } from '../../../../contexts/access/ManagerContext/useManager';

type InputProps = {
  document: IDocument;
  onReject?: (reason: IRejectReasonDocument) => void;
  onApprove?: (data: { [key: string]: any }) => void;
  onUpdate?: (data: { [key: string]: any }) => void;
  onDownload?: () => void;
  onUpdateDocumentImage?: (data: IUpdateDocumentImage) => void;
  onPurge?: (document: IDocument) => void;
  titlePrev: string;
  titleNext: string;
  onPrev: () => void;
  onNext: () => void;
  isLoadingVerificationStatus?: boolean;
  activeTabDocumentFile: string;
  setActiveTabDocumentFile: (v: string) => void;
  setOriginalFileParams: (v: OriginalFileParams) => void;
  currentOriginalFile?: FileDTO;
  isLoadingOriginalFile: boolean;
};

const defaultFile = {
  documentName: 'Verification',
  fileKey: '',
  fileType: '',
  fileUrl: '',
};

export const DOTMedicalCardView = ({
  isLoadingOriginalFile,
  onReject,
  onApprove,
  onUpdate,
  document,
  onDownload,
  onUpdateDocumentImage,
  onPurge,
  titlePrev,
  titleNext,
  onPrev,
  onNext,
  isLoadingVerificationStatus = false,
  activeTabDocumentFile,
  setOriginalFileParams,
  currentOriginalFile,
}: InputProps) => {
  const { isReadOnly } = useManager();
  const [polygon, setPolygon] = useState<any | null>();
  const [locked, setLocked] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [currentDocumentFile, setCurrentDocumentFile] = useState<
    DocumentFiles | undefined
  >();

  const handleCloseEditMode = () => {
    setIsEdit(false);
    setOriginalFileParams(defaultOriginalFileParams);
  };

  useEffect(() => {
    handleCloseEditMode();
  }, [document]);

  useEffect(() => {
    if (activeTabDocumentFile) {
      setCurrentDocumentFile(
        documentFileList.find(
          (document) =>
            document.documentName.toLowerCase() === activeTabDocumentFile
        )
      );
    }
  }, [activeTabDocumentFile]);

  useEffect(() => {
    setLocked(
      ![DocumentStatusEnum.UPLOADED, DocumentStatusEnum.NEEDS_REVIEW].includes(
        document.status
      )
    );
  }, [document]);

  const documentFileList = useMemo(() => {
    const documentFiles = document.documentFiles.filter(
      (docFile) => !docFile.documentName.endsWith(originalSuffix)
    );

    if (documentFiles.length === 2 || isReadOnly) {
      return documentFiles;
    }

    return [documentFiles[0], defaultFile];
  }, [document.documentFiles]);

  const initialValues = useMemo(() => {
    if (!document?.details || !Object.keys(document.details).length) {
      return dotMedicalCardDefault;
    }

    return document.details.reduce(
      (hash: { [key: string]: any }, currentValue: IDocumentDetailField) => {
        if (
          currentValue?.fieldValue &&
          !Number.isNaN(Date.parse(currentValue.fieldValue))
        ) {
          const [date] = currentValue.fieldValue.split('T');
          const [y, m, d] = date.split('-');
          hash[currentValue.fieldName] = `${m}/${d}/${y}`;
        } else {
          hash[currentValue.fieldName] = currentValue.fieldValue || '';
        }

        return hash;
      },
      {}
    );
  }, [document]);

  const fieldVerified = useMemo(() => {
    const fieldVerified = buildFieldVerified(document?.details);
    if (fieldVerified) {
      fieldVerified.examinerLicenseNumber =
        document?.verifications?.DOT_MEDICAL_VERIFICATION_IMAGE
          ?.verificationStatus === DocumentDetailFieldStatusEnum.SUCCESS;
    }
    return fieldVerified ?? dotMedicalCardDefaultVerification;
  }, [document]);

  const fieldPolygon = useMemo(() => {
    const fieldPolygon = buildFieldPolygon(document?.details);
    return fieldPolygon ?? dotMedicalCardDefaultPolygon;
  }, [document]);

  const verificationResult = useMemo(() => {
    return buildVerificationResult(document?.details);
  }, [document]);

  const { getFieldProps, getFieldErrorProp, handleSubmit, isValid } = useFormik(
    {
      initialValues,
      validationSchema,
      enableReinitialize: true,
      onSubmit(value: { [key: string]: any }) {
        onApprove &&
          onApprove({
            ...value,
            expirationDate: value.expirationDate
              ? clientDateToServerDateString(new Date(value.expirationDate))
              : undefined,
            issueDate: value.issueDate
              ? clientDateToServerDateString(new Date(value.issueDate))
              : undefined,
            medExaminerPhone: getFieldProps('medExaminerPhone').value,
            accompaniedByText: getFieldProps('accompaniedByText').value,
          });
      },
    }
  );

  const verificationStatus = useMemo(() => {
    return buildVerificationStatus(document?.details);
  }, [document]);

  const handleApprove = () => {
    handleSubmit();
  };

  const handlePurge = () => {
    onPurge && onPurge(document);
  };

  const handleHoverField = (polygon: any | null) => {
    setPolygon(polygon);
  };

  const handleUnHoverField = () => {
    setPolygon(null);
  };

  const handleVerify = () => {
    setCurrentDocumentFile(
      documentFileList.find(
        (d) => d?.documentName?.toLowerCase() === 'verification'
      ) || documentFileList[0]
    );
    onUpdate &&
      onUpdate({
        examinerLicenseNumber: getFieldProps('examinerLicenseNumber').value,
        expirationDate: getFieldProps('expirationDate').value
          ? clientDateToServerDate(
              new Date(getFieldProps('expirationDate').value)
            )
          : undefined,
        isVerifiedLicenseNumber:
          getFieldProps('verifiedLicenseNumber').value === 'Yes',
      });
  };

  const handleEdit = () => {
    if (currentDocumentFile?.documentName) {
      setOriginalFileParams({
        candidateDocumentId: document.candidateDocumentId,
        fileName: currentDocumentFile?.documentName,
      });
      setIsEdit(true);
    }
  };

  const handleSaveImage = (file: Blob) => {
    handleCloseEditMode();
    if (
      onUpdateDocumentImage &&
      file &&
      document?.candidateDocumentId &&
      currentDocumentFile?.documentName
    ) {
      onUpdateDocumentImage({
        file: new File([file], currentDocumentFile?.documentName, {
          type: file.type,
        }),
        candidateDocumentId: document.candidateDocumentId,
        fileName: currentDocumentFile.documentName,
      });
    }
  };

  const canApprove = useMemo(() => {
    return canApproveDotMedical({
      document,
      isEnabled: true,
      isFormValid: isValid,
    });
  }, [document, isValid]);

  const notifications = useMemo(() => {
    if (document.details && Object.keys(document.details).length) {
      const examinerLicenseNumber = document.details.find(
        (item) => item.fieldName === 'examinerLicenseNumber'
      );
      if (!examinerLicenseNumber?.fieldValue) {
        return [
          {
            message: 'Examiner National Registry Number not found',
            type: 'warning' as AlertColor,
          },
        ];
      }
    }
    if (
      document?.verifications &&
      document.verifications.DOT_MEDICAL_NATIONAL_REGISTRY_NUMBER
    ) {
      if (
        document.verifications.DOT_MEDICAL_NATIONAL_REGISTRY_NUMBER
          .verificationStatus === 'FAILED'
      ) {
        return [
          {
            message:
              document.verifications.DOT_MEDICAL_NATIONAL_REGISTRY_NUMBER
                .verificationResult,
            type: 'warning' as AlertColor,
          },
        ];
      }
    }
    return [];
  }, [document.details]);

  const isReturnToWork = useMemo(() => {
    return !!document?.relatedDocuments?.find(
      (doc) => doc.documentAlias === 'return-to-work'
    );
  }, [document?.relatedDocuments]);

  if (document.status === DocumentStatusEnum.DELETED) {
    return (
      <div className={styles.root}>
        <Divider />
        <DocumentReviewHeader
          titlePrev={titlePrev}
          titleNext={titleNext}
          onPrev={onPrev}
          onNext={onNext}
          title={document.documentName}
          notifications={[]}
          canEditDocumentType={false}
          document={document}
        />
        <div className={styles.content}>
          <div className={styles.purged}>The document has been purged</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Divider />
      <DocumentReviewHeader
        titlePrev={titlePrev}
        titleNext={titleNext}
        onPrev={onPrev}
        onNext={onNext}
        title={
          document.documentName +
          (document.status === DocumentStatusEnum.REJECTED ? ' - REJECTED' : '')
        }
        notifications={notifications}
        canEditDocumentType={false}
        document={document}
      />
      <div className={styles.content}>
        {isReadOnly ? (
          readOnlyViewableStatuses.includes(document.status) ? (
            <DocumentImageViewer
              polygon={polygon}
              currentDocument={currentDocumentFile}
              setCurrentDocument={setCurrentDocumentFile}
              documents={documentFileList}
            />
          ) : (
            <DocumentHiddenIcon />
          )
        ) : (
          <>
            {isEdit ? (
              !isLoadingOriginalFile && currentOriginalFile?.url ? (
                <DocumentImageCropper
                  fileUrl={currentOriginalFile?.url}
                  onSave={handleSaveImage}
                  onClose={handleCloseEditMode}
                  aspectRatio={1.235}
                />
              ) : (
                <AnimatedLoaderModal isLoading={isLoadingOriginalFile} />
              )
            ) : (
              <>
                <div className={styles.cropper}>
                  <DocumentImageViewer
                    polygon={polygon}
                    onDownload={
                      document.status === DocumentStatusEnum.APPROVED
                        ? onDownload
                        : undefined
                    }
                    onPurge={handlePurge}
                    onReject={!locked ? onReject : undefined}
                    onEdit={
                      currentDocumentFile?.fileUrl ? handleEdit : undefined
                    }
                    onApprove={!locked ? handleApprove : undefined}
                    currentDocument={currentDocumentFile}
                    setCurrentDocument={setCurrentDocumentFile}
                    documents={documentFileList}
                    isLoading={isLoadingVerificationStatus}
                    canApprove={canApprove}
                  />
                </div>
                <div className={styles.formWrapper}>
                  <DOTMedicalCardForm
                    getFieldProps={getFieldProps}
                    getFieldErrorProp={getFieldErrorProp}
                    fieldVerified={fieldVerified}
                    fieldPolygon={fieldPolygon}
                    verificationResult={verificationResult || undefined}
                    verificationStatus={verificationStatus || undefined}
                    onHoverField={handleHoverField}
                    onUnHoverField={handleUnHoverField}
                    onVerify={handleVerify}
                    disabled={locked}
                    canApprove={Boolean(canApprove?.status)}
                    isReturnToWork={isReturnToWork}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
      <LogEvents logs={document.documentEvents} />
    </div>
  );
};
